<script setup lang="ts">
import { computed, ref } from 'vue';
import Dialog from 'primevue/dialog';
import RadioButton from 'primevue/radiobutton';
import DatePicker from 'primevue/datepicker';
import MultiSelect from 'primevue/multiselect';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import moment from 'moment';
import { ButtonType, CustomButton } from '@/components';
import { useExportStore } from '@/stores/export';
import { type PostExport, type IForm, type ICase, type ExportColumnHeader } from '@/models';
import { useFormStore } from '@/stores/forms';
import { useCaseStore } from '@/stores/case';
import { useI18n } from 'vue-i18n';
import { useStudyStore } from '@/stores/study';

defineProps({
  visible: {
    type: Boolean,
    required: true
  }
});

const { t: $t } = useI18n();
const formStore = useFormStore();
const exportStore = useExportStore();
const caseStore = useCaseStore();
const studyStore = useStudyStore();

const selectedForm = ref<IForm[] | []>([]);
const selectedCases = ref<ICase[] | null>(null);
const periodType = ref();
const periodDates = ref();
const formatType = ref();
const choiceType = ref();
const includeArchive = ref();
const includeFiles = ref();
const includeMetadata = ref();
const shiftedDates = ref();
const selectedTimezone = ref<string>();
const localTimezoneForSystemDates = ref();

//@ts-ignore shows error in the build process because of the type, but should work.
const timezones = Intl.supportedValuesOf('timeZone');

const columnHeaders = ref<ExportColumnHeader[]>([{
  label: 'Field Label',
  value: 'label'
}, {
  label: 'Field Variable Name',
  value: 'variableName'
}, {
  label: 'Field Identifier',
  value: 'identifier'
}]);

const selectedColumnHeader = ref<ExportColumnHeader>(columnHeaders.value[0]);

const emits = defineEmits(['onClose']);

const closeDialog = () => {
  emits('onClose');
  clearForm();
};

const clearForm = () => {
  selectedForm.value = [];
  selectedCases.value = null;
  periodType.value = undefined;
  periodDates.value = undefined;
  formatType.value = undefined;
  choiceType.value = undefined;
  includeArchive.value = undefined;
  shiftedDates.value = undefined;
  selectedColumnHeader.value = columnHeaders.value[0];
};

const radioChangeListener = () => {
  if (periodType.value === 'All') {
    periodDates.value = null;
  }
};

const createExport = async () => {
  const postExport: PostExport = {
    formIdentifiers: selectedForm.value.map(form => form.identifier),
    choiceLabels: choiceType.value === 'labels',
    format: formatType.value,
    includeArchive: includeArchive.value ?
      includeArchive.value[0] === 'archive' :
      false,
    files: includeFiles.value ? includeFiles.value[0] === 'files' : false,
    metadata: includeMetadata.value ?
      includeMetadata.value[0] === 'metadata' :
      false,
    shiftDates: shiftedDates.value ? shiftedDates.value[0] === 'shifted-dates' : false,
    header: selectedColumnHeader.value.value,
    timezone: localTimezoneForSystemDates.value ? selectedTimezone.value : undefined
  };
  if (selectedCases.value) {
    postExport.caseIdentifiers = selectedCases.value.map(
      caseItem => caseItem.identifier
    );
  }
  if (periodDates.value) {
    postExport.period = {
      start: moment
        .utc(moment(periodDates.value[0]).format('YYYY-MM-DD'))
        .startOf('day')
        .valueOf(),
      end: moment
        .utc(moment(periodDates.value[1]).format('YYYY-MM-DD'))
        .endOf('day')
        .valueOf()
    };
  }
  await exportStore.createStudyExport(postExport);
  closeDialog();
};

const checkPeriodDates = () => {
  const nullValues = periodDates.value ?
    periodDates.value.filter((date: string) => date == null) :
    [];
  return periodDates.value && nullValues.length > 0;
};

const correctPeriodDates = computed(() => checkPeriodDates());

const enableExport = computed(
  () =>
    selectedForm.value.length > 0 &&
    periodType.value !== undefined &&
    (periodType.value === 'Select' ? !checkPeriodDates() : true) &&
    formatType.value !== undefined &&
    choiceType.value !== undefined
);
</script>

<template>
  <Dialog :visible="visible"
    modal
    header="Export"
    class="dialog"
    style="width: 600px"
    @update:visible="closeDialog">
    <span class="p-text-secondary block mb-5">Configure your export</span>
    <div class="group form">
      <div>
        Form
        <template v-if="!formStore.formList.length">
          (Forms not found)
        </template>
      </div>
      <div class="p-dropdown-wrapper">
        <MultiSelect v-model="selectedForm"
          placeholder="Select Forms"
          :options="formStore.formList"
          :disabled="!formStore.formList.length"
          filter
          optionLabel="title"/>
      </div>
    </div>
    <div class="group form">
      <div>Cases</div>
      <div class="p-dropdown-wrapper">
        <MultiSelect v-model="selectedCases"
          placeholder="Select cases"
          :options="caseStore.studyCaseList"
          filter
          optionLabel="label"/>
      </div>
    </div>
    <div class="group period">
      <div>Select period</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="periodType"
            inputId="period1"
            name="all"
            @change="radioChangeListener"
            value="All"/>
          <label for="all" class="ml-2">All</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="periodType"
            inputId="period2"
            name="select"
            @change="radioChangeListener"
            value="Select"/>
          <label for="select" class="ml-2">Select Range</label>
        </div>
      </div>
      <DatePicker v-model="periodDates"
        selectionMode="range"
        :manualInput="false"
        :maxDate="moment().toDate()"
        showIcon
        :disabled="periodType !== 'Select'"/>
    </div>
    <div class="group format">
      <div>File format</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="formatType"
            inputId="format1"
            name="csv"
            value="csv"/>
          <label for="csv" class="ml-2">.csv (Ideal for large export)</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="formatType"
            inputId="format2"
            name="xlsx"
            value="xlsx"/>
          <label for="xlsx" class="ml-2">.xlsx (Excel)</label>
        </div>
      </div>
    </div>
    <div class="group choice">
      <div>Display of choice fields</div>
      <div class="radio-group">
        <div class="radio-button one">
          <RadioButton v-model="choiceType"
            inputId="choice1"
            name="coded"
            value="coded"/>
          <label for="coded" class="ml-2">Coded values (eg. '1')</label>
        </div>
        <div class="radio-button">
          <RadioButton v-model="choiceType"
            inputId="choice2"
            name="labels"
            value="labels"/>
          <label for="labels" class="ml-2">Choice Labels (eg. 'female')</label>
        </div>
      </div>
    </div>
    <div class="group form" v-if="studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP'">
      <div>Column Header</div>
      <div class="p-dropdown-wrapper">
        <Select v-model="selectedColumnHeader"
          placeholder="Select column header"
          :options="columnHeaders"
          optionLabel="label"/>
      </div>
    </div>
    <div class="archive-group-grid">
      <div class="group archive">
        <div class="archive-label">Include archived entries</div>
        <Checkbox v-model="includeArchive"
          inputId="archive"
          name="archvie"
          value="archive"/>
      </div>
      <div class="group archive">
        <div class="archive-label">Include uploaded files</div>
        <Checkbox v-model="includeFiles"
          inputId="files"
          name="files"
          value="files"/>
      </div>
      <div class="group archive">
        <div class="archive-label">Include entry metadata</div>
        <Checkbox v-model="includeMetadata"
          inputId="metadata"
          name="metadata"
          value="metadata"/>
      </div>
      <div class="group archive" v-if="studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP'">
        <div class="shifted-dates-label">Use shifted dates</div>
        <FontAwesomeIcon :icon="[`fas`, `square-info`]"
            v-tooltip="$t('shifted_dates_tooltip')"
            class="shifted-dates-tooltip"/>
        <Checkbox v-model="shiftedDates"
          inputId="shifted-dates"
          name="shifted-dates"
          value="shifted-dates"/>
      </div>
    </div>
    <div class="group form" v-if="studyStore.selectedStudy?.flags?.customDataDictionary !== 'PUMP'">
      <div class="timezone-label">
        <div class="shifted-dates-label">Localize timezone</div>
        <FontAwesomeIcon :icon="[`fas`, `square-info`]"
          v-tooltip="$t('export-local-timezone')"
          class="shifted-dates-tooltip"/>
        
        <Checkbox v-model="localTimezoneForSystemDates"
          binary
          inputId="local-timezone-system-dates"
          name="local-timezone-system-dates"
          value="local-timezone-system-dates"/>
      </div>
      <div class="p-dropdown-wrapper" v-if="localTimezoneForSystemDates">
        <Select v-model="selectedTimezone"
          placeholder="Select timezone"
          filter
          :options="timezones"/>
      </div>
    </div>
    <div class="error">
      <Message severity="error" v-if="correctPeriodDates">
        Please select the correct date "range"
      </Message>
    </div>
    <div class="buttons">
      <CustomButton text="Cancel"
        :type="ButtonType.OUTLINED"
        @onClick="closeDialog"/>
      <CustomButton text="Create Export"
        :type="ButtonType.POSITIVE"
        :marginLeft="16"
        :disabled="!enableExport"
        :loading="exportStore.createExportLoader.isLoading"
        @onClick="createExport"/>
    </div>
  </Dialog>
</template>

<style scoped lang="scss">
@import "../../assets/variables.module";
.group {
  margin-bottom: 16px;
}
.group.form {
  margin-top: 16px;
}
:deep(.p-dropdown) {
  width: 100%;
}
.p-dropdown-wrapper {
  padding-top: 12px;
}
.p-select {
  width: 100%;
}
.period-group {
  margin-bottom: 16px;
}
.radio-group {
  display: flex;
  align-items: center;
  padding: 12px;
}
.radio-button {
  display: flex;
  align-items: center;
}
.radio-button.one {
  margin-right: 32px;
}
.radio-button label {
  margin-left: 8px;
}
:deep(.p-calendar) {
  padding-left: 12px;
  padding-bottom: 12px;

  > .p-button {
    background-color: $primary-color;
    border-color: $primary-color;
  }
}
:deep(.p-calendar:not(.p-calendar-disabled).p-focus > .p-inputtext) {
  outline-color: $primary-color;
}
:deep(.p-inputtext) {
  width: 220px;
}
.format-group {
  margin-bottom: 16px;
}
.radio-group.choice {
  flex-direction: column;
  align-items: flex-start;

  .radio-button.one {
    margin-bottom: 12px;
  }
}
.group.archive {
  display: flex;
  align-items: center;

  .archive-label {
    margin-right: 16px;
  }
}
.shifted-dates-label {
  margin-right: 4px;
}
.shifted-dates-tooltip {
  margin-right: 16px;
}
:deep(
    .p-checkbox:not(.p-disabled):has(.p-checkbox-input:checked).p-highlight
      .p-checkbox-box
  ) {
  background-color: $primary-color !important;
  border-color: $primary-color !important;
}
.buttons {
  display: flex;
  justify-content: flex-end;
}
.error {
  margin-bottom: 16px;
}
:deep(.p-message-text) {
  flex: 1;
  margin-left: 16px;
}
.p-multiselect {
  width: 100%;
}
.archive-group-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 24px;
  margin-bottom: 16px;
}
.timezone-label {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
</style>
